'use client';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { doc, updateDoc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db, auth } from './firebaseConfig';
import SubscriptionUpgradePanel from './components/SubscriptionUpgradePanel';
import CountrySelectionModal from './components/CountrySelectionModal';
import PhoneNumberSearchModal from './components/PhoneNumberSearchModal';
import TollFreeSearchModal from './components/TollFreeSearchModal';
import PhoneNumberSuccessModal from './components/PhoneNumberSuccessModal';
import CountryFlag from './components/CountryFlag';
import { 
  getCurrentSubscriptionInfo, 
  checkPhoneLineEligibility, 
  upgradeSubscription 
} from './utils/subscriptionUtils';

export default function PhoneRelayList({ userDoc, onSelect, onRefresh, autoOpenModal }) {
  const [isFetchingNumber, setIsFetchingNumber] = useState(false);
  const [showUpgradePanel, setShowUpgradePanel] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showNumberSearchModal, setShowNumberSearchModal] = useState(false);
  const [showTollFreeSearchModal, setShowTollFreeSearchModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [lineEligibility, setLineEligibility] = useState(null);
  const [phoneCountries, setPhoneCountries] = useState({});

  // Load subscription info when component mounts or userDoc changes
  useEffect(() => {
    const loadSubscriptionInfo = async () => {
      if (userDoc?.uid) {
        try {
          const subInfo = await getCurrentSubscriptionInfo(userDoc.uid);
          setSubscriptionInfo(subInfo);
          
          const eligibility = checkPhoneLineEligibility(userDoc, subInfo);
          setLineEligibility(eligibility);
        } catch (error) {
          console.error('Error loading subscription info:', error);
        }
      }
    };

    loadSubscriptionInfo();
  }, [userDoc]);


  // Load country information for phone aliases
  useEffect(() => {
    const loadPhoneCountries = async () => {
      if (userDoc?.phoneAliases?.length > 0) {
        const countries = {};
        for (const phoneAlias of userDoc.phoneAliases) {
          try {
            const numberDoc = await getDoc(doc(db, 'userNumbers', phoneAlias.aliasNumber));
            if (numberDoc.exists()) {
              countries[phoneAlias.aliasNumber] = numberDoc.data().country || 'US';
            } else {
              countries[phoneAlias.aliasNumber] = 'US'; // Default to US if not found
            }
          } catch (error) {
            console.error(`Error fetching country for ${phoneAlias.aliasNumber}:`, error);
            countries[phoneAlias.aliasNumber] = 'US'; // Default to US on error
          }
        }
        setPhoneCountries(countries);
      }
    };

    loadPhoneCountries();
  }, [userDoc?.phoneAliases]);

  // Auto-open country modal when coming from overview and user has no phone numbers
  useEffect(() => {
    if (autoOpenModal && userDoc && (!userDoc.phoneAliases || userDoc.phoneAliases.length === 0)) {
      console.log('Auto-opening country modal for new phone number creation');
      setShowCountryModal(true);
    }
  }, [autoOpenModal, userDoc]);


  const handleGetNumber = () => {
    console.log('handleGetNumber called');
    console.log('lineEligibility:', lineEligibility);
    
    // Check if user has reached their phone line limit
    if (!lineEligibility?.canAdd) {
      console.log('Cannot add line, showing upgrade panel');
      setShowUpgradePanel(true);
      return;
    }

    // Show country selection modal
    console.log('Setting showCountryModal to true');
    setShowCountryModal(true);
  };

  const handleCountrySelection = async (selectedCountry) => {
    console.log("handleCountrySelection: Starting process to fetch an available number from", selectedCountry);
    setIsFetchingNumber(true);
    try {
      // Use userDoc.uid if available; otherwise, try auth.currentUser.uid.
      const userId = (userDoc && userDoc.uid)
        ? userDoc.uid
        : (auth.currentUser ? auth.currentUser.uid : null);
      console.log("handleCountrySelection: Determined userId =", userId);
      if (!userId) {
        throw new Error("Missing userDoc or uid; cannot update alias document.");
      }

      console.log(`handleCountrySelection: Sending request to /api/getAvailableNumber?country=${selectedCountry}...`);
      const response = await fetch(`/api/getAvailableNumber?country=${selectedCountry}`);
      console.log("handleCountrySelection: Response status =", response.status);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`No phone numbers are currently available for ${selectedCountry}. Please contact support for assistance.`);
        }
        const errorText = await response.text();
        console.error("handleCountrySelection: API error:", errorText);
        throw new Error(errorText || 'Failed to fetch available number from API');
      }

      const data = await response.json();
      console.log("handleCountrySelection: Data received from API:", data);
      const number = data.number;
      const telnyxID = data.telnyxID;
      if (!number || !telnyxID) {
        throw new Error('API returned incomplete data: number or telnyxID is missing.');
      }
      console.log(`handleCountrySelection: Retrieved number ${number} with telnyxID ${telnyxID}`);

      // Create a new document in the phoneAliases subcollection
      const phoneAliasRef = doc(db, 'userAliases', userId, 'phoneAliases', number);
      await setDoc(phoneAliasRef, {
        aliasNumber: number,
        telnyxID: telnyxID,
        forwardTo: userDoc?.forwardToNumber || '',
        connectionStatus: 'Ready',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      console.log("handleCountrySelection: Phone alias document created successfully.");

      // Mark the number as unavailable in the userNumbers collection.
      console.log("handleCountrySelection: Marking number as unavailable.");
      const numberRef = doc(db, 'userNumbers', number);
      await updateDoc(numberRef, { status: 'unavailable' });
      console.log("handleCountrySelection: Number marked as unavailable.");
      
      // Update local state with the new phone country
      setPhoneCountries(prev => ({
        ...prev,
        [number]: selectedCountry
      }));
      
      // Store the new number and country for the success modal
      setNewPhoneNumber(number);
      setSelectedCountry(selectedCountry);
      
      // Close the country modal and show the success modal
      setShowCountryModal(false);
      setShowSuccessModal(true);
      
      // Call the onRefresh callback (if provided)
      if (onRefresh) {
        console.log("handleCountrySelection: Calling onRefresh to update the PhoneRelayList.");
        onRefresh();
      }
      
    } catch (error) {
      console.error("Error getting a number:", error);
      setShowCountryModal(false);
    } finally {
      setIsFetchingNumber(false);
      console.log("handleCountrySelection: Process completed.");
    }
  };

  const handleUpgrade = async (selectedPlan) => {
    try {
      const result = await upgradeSubscription(userDoc.uid, selectedPlan);
      console.log('Subscription upgraded successfully:', result);
      
      
      // Refresh user data to reflect new subscription limits
      if (onRefresh) {
        onRefresh();
      }
      
      // Reload subscription info
      const subInfo = await getCurrentSubscriptionInfo(userDoc.uid);
      setSubscriptionInfo(subInfo);
      
      const eligibility = checkPhoneLineEligibility(userDoc, subInfo);
      setLineEligibility(eligibility);
      
      // Close upgrade panel
      setShowUpgradePanel(false);
      
      // Now attempt to get the number
      await handleGetNumber();
      
    } catch (error) {
      console.error('Upgrade failed:', error);
      throw error; // Let the upgrade panel handle the error display
    }
  };

  const handleShowNumberSearch = () => {
    setShowNumberSearchModal(true);
  };

  const handleShowTollFreeSearch = () => {
    setShowTollFreeSearchModal(true);
  };

  const handleNumberSearchSelect = async (result) => {
    console.log('Number search result:', result);
    
    if (result.success) {
      // Update local state with the new phone country
      setPhoneCountries(prev => ({
        ...prev,
        [result.phoneNumber]: 'US'
      }));
      
      // Store the new number for the success modal
      setNewPhoneNumber(result.phoneNumber);
      setSelectedCountry('US');
      
      // Close the search modal and show the success modal
      setShowNumberSearchModal(false);
      setShowSuccessModal(true);
      
      // Call the onRefresh callback
      if (onRefresh) {
        console.log('Calling onRefresh to update the PhoneRelayList.');
        onRefresh();
      }
    } else {
      setShowNumberSearchModal(false);
    }
  };

  const handleTollFreeSearchSelect = async (result) => {
    console.log('Toll-free search result:', result);
    
    if (result.success) {
      // Update local state with the new phone country
      setPhoneCountries(prev => ({
        ...prev,
        [result.phoneNumber]: 'US'
      }));
      
      // Store the new number for the success modal
      setNewPhoneNumber(result.phoneNumber);
      setSelectedCountry('US');
      
      // Close the toll-free search modal and show the success modal
      setShowTollFreeSearchModal(false);
      setShowSuccessModal(true);
      
      // Call the onRefresh callback
      if (onRefresh) {
        console.log('Calling onRefresh to update the PhoneRelayList.');
        onRefresh();
      }
    } else {
      setShowTollFreeSearchModal(false);
    }
  };

  // If no phone aliases exist, render the UI to get a number.
  if (!userDoc || !userDoc.phoneAliases || userDoc.phoneAliases.length === 0) {
    console.log("PhoneRelayList: No alias found. Rendering 'Get a number' UI.");
    return (
      <div className="h-auto flex flex-col items-center justify-center py-8">
        {isFetchingNumber ? (
          <button
            type="button"
            disabled
            className="bg-indigo-500/20 px-4 py-3 rounded-lg text-sm font-medium text-indigo-300 backdrop-blur flex items-center"
          >
            <svg
              className="mr-3 h-5 w-5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                className="opacity-25"
              />
              <path
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                fill="currentColor"
                className="opacity-75"
              />
            </svg>
            Getting you a number…
          </button>
        ) : (
          <button
            type="button"
            onClick={handleGetNumber}
            className="rounded-lg bg-indigo-500/20 px-4 py-3 text-sm font-medium text-indigo-300 backdrop-blur hover:bg-indigo-500/30 transition-all duration-200"
          >
            Get a number
          </button>
        )}

        {/* Always-mounted modals */}
        <SubscriptionUpgradePanel
          isOpen={showUpgradePanel}
          onClose={() => setShowUpgradePanel(false)}
          onUpgrade={handleUpgrade}
          requiredLines={(lineEligibility?.currentLines || 0) + 1}
          currentSubscription={subscriptionInfo}
        />

        <CountrySelectionModal
          isOpen={showCountryModal}
          onClose={() => {
            console.log('Closing country modal');
            setShowCountryModal(false);
          }}
          onSelectCountry={handleCountrySelection}
          onShowNumberSearch={handleShowNumberSearch}
          onShowTollFreeSearch={handleShowTollFreeSearch}
          isLoading={isFetchingNumber}
          subscriptionInfo={subscriptionInfo}
          userId={userDoc?.uid}
        />

        <PhoneNumberSearchModal
          isOpen={showNumberSearchModal}
          onClose={() => setShowNumberSearchModal(false)}
          onSelectNumber={handleNumberSearchSelect}
          userId={userDoc?.uid || ''}
        />

        <TollFreeSearchModal
          isOpen={showTollFreeSearchModal}
          onClose={() => setShowTollFreeSearchModal(false)}
          onSelectNumber={handleTollFreeSearchSelect}
          userId={userDoc?.uid || ''}
        />

        <PhoneNumberSuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          phoneNumber={newPhoneNumber}
          country={selectedCountry}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  // If phone aliases exist, display them.
  return (
    <div className="max-h-[calc(100vh-200px)] overflow-hidden flex flex-col">
      <div className="flex-1 overflow-y-auto">
        <ul className="divide-y divide-white/10">
        {userDoc.phoneAliases.map((phoneAlias) => {
          let badgeClasses = '';
          if (phoneAlias.connectionStatus === 'Active') {
            badgeClasses =
              'inline-flex items-center rounded-md bg-green-500/10 px-2 py-1 text-xs font-medium text-green-400 ring-1 ring-green-500/20 ring-inset';
          } else if (phoneAlias.connectionStatus === 'Ready') {
            badgeClasses =
              'inline-flex items-center rounded-md bg-red-500/10 px-2 py-1 text-xs font-medium text-red-400 ring-1 ring-red-500/20 ring-inset';
          } else {
            badgeClasses =
              'inline-flex items-center rounded-md bg-gray-500/10 px-2 py-1 text-xs font-medium text-gray-400 ring-1 ring-gray-500/20 ring-inset';
          }

          return (
            <li key={phoneAlias.aliasNumber}>
              <button
                className="w-full flex items-center justify-between py-3 cursor-pointer hover:bg-white/5 px-3 rounded-md transition-all duration-200 bg-transparent border-none text-left"
                onClick={() => onSelect(phoneAlias)}
                aria-label={`Select phone relay ${phoneAlias.aliasNumber}`}
              >
              <div>
                  <p className="text-sm font-medium text-white">{phoneAlias.aliasNumber}</p>
                {phoneAlias.forwardTo && (
                    <p className="text-xs text-white/60">
                    Phone: {phoneAlias.forwardTo}
                  </p>
                )}
                  <p className="text-xs text-white/60">
                  Status: {phoneAlias.connectionStatus}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <CountryFlag 
                  countryCode={phoneCountries[phoneAlias.aliasNumber] || 'US'} 
                  size="sm" 
                />
                <span className={badgeClasses}>
                  {phoneAlias.connectionStatus === 'Ready' ? 'Inactive' : phoneAlias.connectionStatus}
                </span>
              </div>
              </button>
            </li>
          );
        })}
      </ul>
      </div>
      <div className="border-t border-white/10 pt-4 mt-4">
      <button
        onClick={handleGetNumber}
        type="button"
          className="w-full rounded-lg bg-indigo-500/20 px-3 py-2 text-sm font-medium text-indigo-300 backdrop-blur hover:bg-indigo-500/30 transition-all duration-200"
      >
        + Get Phone Number
      </button>
      </div>
      
      {/* Subscription Upgrade Panel */}
      <SubscriptionUpgradePanel
        isOpen={showUpgradePanel}
        onClose={() => setShowUpgradePanel(false)}
        onUpgrade={handleUpgrade}
        requiredLines={(lineEligibility?.currentLines || 0) + 1}
        currentSubscription={subscriptionInfo}
      />

      {/* Country Selection Modal */}
      <CountrySelectionModal
        isOpen={showCountryModal}
        onClose={() => {
          console.log('Closing country modal');
          setShowCountryModal(false);
        }}
        onSelectCountry={handleCountrySelection}
        onShowNumberSearch={handleShowNumberSearch}
        onShowTollFreeSearch={handleShowTollFreeSearch}
        isLoading={isFetchingNumber}
        subscriptionInfo={subscriptionInfo}
        userId={userDoc?.uid}
      />

      {/* Phone Number Search Modal */}
      <PhoneNumberSearchModal
        isOpen={showNumberSearchModal}
        onClose={() => setShowNumberSearchModal(false)}
        onSelectNumber={handleNumberSearchSelect}
        userId={userDoc?.uid || ''}
      />

      {/* Toll-Free Search Modal */}
      <TollFreeSearchModal
        isOpen={showTollFreeSearchModal}
        onClose={() => setShowTollFreeSearchModal(false)}
        onSelectNumber={handleTollFreeSearchSelect}
        userId={userDoc?.uid || ''}
      />

      {/* Phone Number Success Modal */}
      <PhoneNumberSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        phoneNumber={newPhoneNumber}
        country={selectedCountry}
        onRefresh={onRefresh}
      />
    </div>
  );
}

PhoneRelayList.propTypes = {
  userDoc: PropTypes.shape({
    uid: PropTypes.string,
    phoneAliases: PropTypes.array,
    forwardToNumber: PropTypes.string
  }),
  onSelect: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,
  autoOpenModal: PropTypes.bool
};